import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@fragments';
import Parser from '@helpers/parser';
import { getIntlLocale } from '@i18n';
import {
    IconCopy,
    IconClock,
    IconExternalLink,
    IconTag,
    IconUser,
    IconCornerDownLeft,
} from '@tabler/icons-react';
import { getChargebackReason } from '../../../enums/chargebackReasons';
import './styles.scss';

function CopyIcon({ value }) {
    const { t } = useTranslation('banking');

    if (!value) return null;
    return (
        <IconCopy
            className="sidebar-copy-btn"
            onClick={() => {
                navigator.clipboard.writeText(String(value)).catch(() => { });
                if (window.setSnackbar) window.setSnackbar(t('common:actions.copied'));
            }}
            size={13}
        />
    );
}

function Section({ icon, title, children, animationDelay = 0 }) {
    return (
        <div className="sidebar-section" style={{ animationDelay: `${animationDelay}ms` }}>
            <Text.h4 weight={400} contrast={7} className="sidebar-section__title" left={icon}>
                {title}
            </Text.h4>
            <div className="sidebar-section__body">{children}</div>
        </div>
    );
}

function Row({ label, children }) {
    return (
        <div className="sidebar-detail-row">
            <Text.span weight={400} contrast={6} className="sidebar-detail-row__label">
                {label}
            </Text.span>
            <div className="sidebar-detail-row__value">{children}</div>
        </div>
    );
}

function Value({ children, left, copy, className, ...props }) {
    return (
        <div className={`sidebar-detail-value ${className || ''}`}>
            {left && React.cloneElement(left, { size: left.props.size || 13, style: { opacity: 0.6, ...left.props.style } })}
            <Text.span weight={300} contrast={5} {...props}>
                {children}
            </Text.span>
            {copy && <CopyIcon value={copy} />}
        </div>
    );
}

function formatDeadline(deadline) {
    if (!deadline) return '—';
    return new Date(deadline).toLocaleDateString(getIntlLocale(), {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
}

export default function DetailsSidebar({ chargeback }) {
    const { t } = useTranslation('banking');
    const reasonData = useMemo(
        () => getChargebackReason(t, chargeback.reason),
        [t, chargeback.reason],
    );
    const deadline = chargeback.timestamps?.deadline;
    const hasDisputeDocuments = chargeback.dispute?.documents?.length > 0;
    const firstDocument = chargeback.dispute?.documents?.[0];

    const createdEvent = (chargeback.events || []).find(e => e.type === 'created');
    const providerChargebackId = createdEvent?.metadata?.id || null;

    return (
        <div className="details-sidebar">
            <Section icon={<IconCornerDownLeft />} title={t('chargebacks.sidebar.sectionContestation')} animationDelay={100}>
                <Row label={t('chargebacks.sidebar.contestationId')}>
                    <Value copy={chargeback.id}>
                        {`${chargeback.id}`}
                    </Value>
                </Row>

                <Row label={t('common:fields.value')}>
                    <Value>
                        {Parser.price.format(chargeback.amount, chargeback.currency)}
                    </Value>
                </Row>

                <Row label={t('chargebacks.sidebar.deadline')}>
                    <Value left={<IconClock />}>
                        {formatDeadline(deadline)}
                    </Value>
                </Row>

                {hasDisputeDocuments && (
                    <Row label={t('chargebacks.sidebar.documents')}>
                        {
                            chargeback.dispute?.documents?.map((document, index) => (
                                <a
                                    href={document.path}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="sidebar-link"
                                >
                                    <Value left={<IconExternalLink />}>
                                        {t('chargebacks.sidebar.downloadDocument')}
                                    </Value>
                                </a>
                            ))
                        }
                    </Row>
                )}

                <Row label={t('chargebacks.sidebar.reason')}>
                    <Value className="sidebar-reason-label">
                        {reasonData.label}
                    </Value>
                </Row>

                {chargeback.category && (
                    <Row label={t('common:fields.category')}>
                        <Value>{chargeback.category}</Value>
                    </Row>
                )}
            </Section>

            <div className="sidebar-divider" />

            <Section icon={<IconTag />} title={t('chargebacks.sidebar.sectionCharge')} animationDelay={200}>
                <Row label={t('chargebacks.sidebar.chargeId')}>
                    <Value copy={chargeback.charge}>#{chargeback.charge}</Value>
                </Row>

                {chargeback.plan && (
                    <Row label={t('common:entities.plan')}>
                        <Value>{chargeback.plan.label}</Value>
                    </Row>
                )}

                {chargeback.plan?.interval && (
                    <Row label={t('chargebacks.sidebar.recurrence')}>
                        <Value>
                            {Parser.interval?.adjective(chargeback.plan.interval) || chargeback.plan.interval}
                        </Value>
                    </Row>
                )}
            </Section>

            <div className="sidebar-divider" />

            <Section icon={<IconUser />} title={t('chargebacks.sidebar.sectionCustomer')} animationDelay={300}>
                {chargeback.user ? (
                    <>
                        <Row label={t('common:fields.name')}>
                            <Value>
                                {chargeback.user.username || '—'}
                            </Value>
                        </Row>

                        <Row label={t('common:fields.email')}>
                            <Value copy={chargeback.user.email}>
                                {chargeback.user.email || '—'}
                            </Value>
                        </Row>

                        <Row label={t('common:fields.phone')}>
                            <Value
                                left={<img src={`https://flagcdn.com/${Parser.getCountryIso(chargeback.user.phone)}.svg`} width="16" alt="" style={{ borderRadius: 2, transform: 'translateY(-1px)' }} />}
                                copy={Parser.phone(`${chargeback.user.phone.areaCode}${chargeback.user.phone.number}`)}
                            >
                                  {Parser.phone(`${chargeback.user.phone.areaCode}${chargeback.user.phone.number}`)}
                            </Value>
                        </Row>
                    </>
                ) : (
                    <Value>{t('chargebacks.sidebar.customerUnavailable')}</Value>
                )}
            </Section>
        </div>
    );
}

DetailsSidebar.Section = Section;
DetailsSidebar.Row = Row;
DetailsSidebar.Value = Value;
